import React from 'react';
import { Button } from './ui/button';
import { AlertTriangle, Trash2, CheckCircle } from 'lucide-react';

/**
 * Reusable confirmation modal component
 * Replaces window.confirm() which doesn't work in sandboxed environments
 */
const ConfirmModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger', // 'danger', 'warning', 'success'
  loading = false
}) => {
  if (!isOpen) return null;

  const variantStyles = {
    danger: {
      headerBg: 'bg-gradient-to-r from-red-500 to-red-600',
      icon: <Trash2 className="w-6 h-6" />,
      buttonBg: 'bg-red-600 hover:bg-red-700'
    },
    warning: {
      headerBg: 'bg-gradient-to-r from-yellow-500 to-orange-500',
      icon: <AlertTriangle className="w-6 h-6" />,
      buttonBg: 'bg-yellow-600 hover:bg-yellow-700'
    },
    success: {
      headerBg: 'bg-gradient-to-r from-green-500 to-emerald-600',
      icon: <CheckCircle className="w-6 h-6" />,
      buttonBg: 'bg-green-600 hover:bg-green-700'
    }
  };

  const style = variantStyles[variant] || variantStyles.danger;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40" 
        onClick={onClose}
      />
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className={`${style.headerBg} px-6 py-4 rounded-t-lg`}>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              {style.icon}
              {title}
            </h2>
          </div>

          <div className="p-6">
            <p className="text-gray-700 whitespace-pre-line">{message}</p>
          </div>

          <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              disabled={loading}
              onClick={onClose}
            >
              {cancelText}
            </Button>
            <Button
              onClick={onConfirm}
              disabled={loading}
              className={`${style.buttonBg} text-white`}
            >
              {loading ? 'Processing...' : confirmText}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConfirmModal;
